import React, { useState } from 'react';
import styles from './CaseList.module.scss';
import { CaseRowType } from './types/CaseRowType';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { DataTableHeader } from '../../../core/components/Table/types/DataTableHeader';
import TextLabelValue from '../../../core/components/TextLabelValue/TextLabelValue';
import DateTimeParser from '../../../core/utils/DateTimeParser';
import {
  Alert,
  Button,
  CheckboxField,
  Form,
  Icon,
  Loader,
  ModuleContainer,
  Table,
} from '../../../core/components';
import { InfoMessageService, TranslationService } from '../../../core/services';
import Status from '../../../core/types/Status';
import ApiProvider from '../../../core/api/ApiProvider';
import { DataTable } from '../../../core/components/Table/types/DataTable';
import { Link, useHistory } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import { useQuery } from 'react-query';

type CaseListData = {
  table: DataTable;
  showViewButton: boolean;
  expandedRow: DataTableHeader[];
  hasMultipleDebtorRecords: boolean;
};

const CaseList = (props: {} & AbortRequestPropsType) => {
  const context = React.useContext(AppContext);
  const history = useHistory();
  const [tableRows, setTableRows] = useState<CaseRowType[]>([]);
  const [expandedRow, setExpandedRow] = useState<DataTableHeader[]>([]);
  const [tableHeaders, setTableHeaders] = useState<DataTableHeader[]>([]);
  const [isShowingAllCases, setIsShowingAllCases] = useState<boolean>(false);
  const [hasMultipleDebtorRecords, setHasMultipleDebtorRecords] = useState<boolean>(false);

  const expandRow = {
    showExpandColumn: true,
    expandColumnPosition: 'right',
    onlyOneExpanding: true,
    expandByColumnOnly: false,
    parentClassName: styles.parentExpand,
    expandHeaderColumnRenderer: () => {
      return '';
    },
    expandColumnRenderer: ({ expanded }: any) => {
      if (expanded) {
        return (
          <Icon
            iconVariant={Icon.Variants.FontAwesome}
            className={styles.linkIcon}
            icon="fas fa-caret-up"
          />
        );
      }
      return (
        <Icon
          iconVariant={Icon.Variants.FontAwesome}
          className={styles.linkIcon}
          icon="fas fa-caret-down"
        />
      );
    },
    renderer: (row: CaseRowType) => (
      <div className={styles.expandedRowWrapper}>
        {expandedRow.map((e: DataTableHeader) => (
          <div key={e.columnName} className={`mb-2 ${styles.expandedRowItem}`}>
            {e.isVisible && (
              <TextLabelValue label={e.headerName}>
                {e.columnName === 'RegisterDate' ? (
                  DateTimeParser.toLocaleDateString(row[e.columnName])
                ) : (
                  <span className={styles.itemText}>
                    {
                      // @ts-ignore
                      row[e.columnName]
                    }
                  </span>
                )}
              </TextLabelValue>
            )}
          </div>
        ))}
      </div>
    ),
  };

  const prepareTable = (headers: DataTableHeader[], showViewButton: boolean) => {
    const newHeaders = headers.map((h: DataTableHeader) => {
      h.cssClassNames = `${!h.isVisible && 'hide-d'} ${!h.isVisibleOnMobile && 'hide-m'}`;

      h.isSortColumn = true;

      if (h.columnName === 'RegisterDate') {
        h.formatter = (value: any) => {
          return DateTimeParser.toLocaleDateString(value);
        };
      }

      if (h.columnName === 'BalanceAmount' || h.columnName === 'OrgClaim') {
        h.cssClassNames = `${h.cssClassNames} ${styles.toRight}`;
      }

      if (h.columnName === 'CaseNo') {
        h.cssClassNames = 'featured';
        h.formatter = (value: any, row: CaseRowType) => {
          return <Link to={`CaseDetails/${row.CaseGuid}`} id={`CaseDetails-${row.CaseNo}`}>{row.CaseNo}</Link>;
        };
      }

      return h;
    });

    if (showViewButton) {
      newHeaders.push({
        columnName: 'Actions',
        cssClassNames: '',
        headerName: '',
        isButtonColumn: false,
        isColumnId: true,
        isDocumentLink: false,
        isSortColumn: false,
        isVisible: true,
        isVisibleOnMobile: true,
        sortColumnNumber: 0,
        formatter: (value: any, row: any) => (
          <div>
            <Button
              variant={Button.Variants.Primary}
              size={Button.Sizes.SM}
              type={Button.Types.Button}
              isUppercase
              onClick={() => {
                history.push(`CaseDetails/${row.CaseGuid}`);
              }}
            >
              {TranslationService.translate('CaseList_ViewButtonLabel')}
            </Button>
          </div>
        ),
      });
    }

    return newHeaders;
  };

  const handleCasesSuccess = (result: Status<CaseListData>) => {
    if (!result.success) {
      return;
    }

    const headers = prepareTable(result.data.table.headers, result.data.showViewButton);
    const rows = result.data.table.dataRows.map((r) => ({
      ...r,
      RegisterDate: DateTimeParser.toValueOf(r.RegisterDate),
    }));

    setTableRows(rows);
    setTableHeaders(headers);
    setExpandedRow(result.data.expandedRow);
    setHasMultipleDebtorRecords(result.data.hasMultipleDebtorRecords);
  };

  const sharedCasesOptions = {
    refetchOnWindowFocus: false as const,
    onSuccess: handleCasesSuccess,
    onError: () => {
      InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
    },
  };

  const openedCases = useQuery<Status<CaseListData>>(
    'openedCases',
    async () =>
      await ApiProvider.get<Status<CaseListData>>(
        'CaseListModule/getCases',
        props.cancelTokenSource.token
      ),
    {
      enabled: !context.isHideMypageCases && isShowingAllCases,
      ...sharedCasesOptions,
    }
  );

  const cases = useQuery<Status<CaseListData>>(
    'cases',
    async () =>
      await ApiProvider.get<Status<CaseListData>>(
        'CaseListModule/getOpenedCases',
        props.cancelTokenSource.token
      ),
    {
      enabled: !context.isHideMypageCases && !isShowingAllCases,
      ...sharedCasesOptions,
    }
  );

  const handleShowAllCases = async () => {
    setIsShowingAllCases(!isShowingAllCases);
  };

  return (
    <ModuleContainer id={'CaseListWrapper'} classNames={styles.wrapper}>
      <ModuleContainer.Header
        classNames={styles.header}
        title={TranslationService.translate('CaseList')}
        headingLevel="h2"
      />

      <ModuleContainer.Body>
        {(cases.isFetching || openedCases.isFetching) && <Loader opacity={0.4} small={true} />}

        {hasMultipleDebtorRecords && (
          <Alert variant={Alert.Variants.Info} icon={true} className="mb-2">
            {TranslationService.translate('CaseList_MultipleDebtorRecordsInfo')}
          </Alert>
        )}

        <Form.Group variant={Form.Group.Variants.Inline}>
          <CheckboxField
            checked={isShowingAllCases}
            name="showAllCases"
            id="showAllCases"
            label={TranslationService.translate('ShowAllCases')}
            onChange={handleShowAllCases}
          />
        </Form.Group>

        <Table
          headers={tableHeaders}
          rows={tableRows}
          wrapperClassNames="has-h-scrollbar"
          className={styles.table}
          keyField="CaseGuid"
          paginationProps={{
            hideSizePerPage: true,
            hidePageListOnlyOnePage: true,
          }}
          expandRow={expandRow}
        />
      </ModuleContainer.Body>
    </ModuleContainer>
  );
};

export default withAbortRequest(CaseList);
